/**
 * Servicio de Gestión de Fincas - V2
 * 
 * Este servicio maneja:
 * - Creación y gestión de fincas catastrales
 * - Interfaz de usuario para gestionar fincas
 * - Importación/exportación de fincas a CSV
 * - Configuración de propiedades de fincas
 */

import React from 'react';
import { useUIStore, useFincasStore } from '../../stores';

// Importar la interfaz Finca desde el store
import { Finca } from '../../stores/fincasStore';

// Importar estilos
import './fincasManager.css';

export class FincasManagerService {
  constructor() {
    useFincasStore.getState().initializeFincas();
  }

  /**
   * Obtiene todas las fincas
   */
  getFincas(): Finca[] {
    return useFincasStore.getState().getAllFincas();
  }

  /**
   * Obtiene una finca por ID
   */
  getFincaById(id: string): Finca | null {
    return useFincasStore.getState().getFincaById(id);
  }

  /**
   * Crea una nueva finca
   */
  createFinca(fincaData: Omit<Finca, 'id' | 'numeroFinca' | 'fechaCreacion' | 'fechaModificacion'>): Finca {
    return useFincasStore.getState().createFinca(fincaData);
  }

  /**
   * Actualiza una finca existente
   */
  updateFinca(id: string, updates: Partial<Omit<Finca, 'id' | 'numeroFinca' | 'fechaCreacion'>>): Finca | null {
    return useFincasStore.getState().updateFinca(id, updates);
  }

  /**
   * Elimina una finca
   */
  deleteFinca(id: string): boolean {
    return useFincasStore.getState().deleteFinca(id);
  }

  /**
   * Actualiza un campo específico de una finca en tiempo real
   */
  private updateFincaField(id: string, field: keyof Finca, value: any): void {
    const finca = this.getFincaById(id);
    if (!finca) return;

    const updates: any = { [field]: value };
    this.updateFinca(id, updates);
  }

  /**
   * Guarda los cambios de una finca
   */
  private saveFincaChanges(id: string): void {
    const { addNotification } = useUIStore.getState();
    const finca = this.getFincaById(id);
    
    if (!finca) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Finca no encontrada'
      });
      return;
    }

    addNotification({
      type: 'success',
      title: 'Cambios guardados',
      message: 'Los cambios se han guardado exitosamente'
    });
  }

  /**
   * Exporta las fincas a CSV
   */
  exportToCSV(): string {
    return useFincasStore.getState().exportToCSV();
  }

  /**
   * Importa fincas desde CSV
   */
  importFromCSV(csvContent: string): { success: number; errors: string[] } {
    return useFincasStore.getState().importFromCSV(csvContent);
  }

  /**
   * Muestra el gestor de fincas
   */
  showFincasManager(): void {
    const { openModal } = useUIStore.getState();

    openModal({
      id: 'fincas-manager',
      title: 'Gestión de Fincas',
      content: this.renderFincasManagerContent(),
      size: 'large'
    });
  }

  /**
   * Crea una nueva finca directamente sin mostrar modal
   */
  private createNewFinca(): void {
    const { addNotification } = useUIStore.getState();
    
    try {
      const nuevaFinca = this.createFinca({ nombre: '' });
      addNotification({
        type: 'success',
        title: 'Finca creada',
        message: 'Se ha creado una nueva finca'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo crear la finca'
      });
    }
  }

  /**
   * Renderiza el contenido del gestor de fincas
   */
  private renderFincasManagerContent(): React.ReactNode {
    return <FincasManagerContent />;
  }
}

// Componente React para renderizar el contenido del gestor de fincas
const FincasManagerContent: React.FC = () => {
  const fincas = useFincasStore(state => state.fincas);
  const { createFinca, updateFinca, deleteFinca } = useFincasStore();
  const { addNotification, openModal } = useUIStore();
  
  // Estado para manejar la ordenación
  const [sortConfig, setSortConfig] = React.useState<{
    key: 'numeroFinca' | 'nombre' | null;
    direction: 'asc' | 'desc';
  }>({ key: 'numeroFinca', direction: 'asc' }); // Inicialmente ordenado por número de finca ascendente
  
  const handleCreateNewFinca = () => {
    try {
      createFinca({ nombre: '' });
      addNotification({
        type: 'success',
        title: 'Finca creada',
        message: 'Se ha creado una nueva finca'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo crear la finca'
      });
    }
  };

  const handleUpdateFincaField = (id: string, field: keyof Finca, value: any) => {
    const updates: any = { [field]: value };
    updateFinca(id, updates);
  };

  const handleSaveFincaChanges = (id: string) => {
    const finca = fincas.find(f => f.id === id);
    if (!finca) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Finca no encontrada'
      });
      return;
    }

    addNotification({
      type: 'success',
      title: 'Cambios guardados',
      message: 'Los cambios se han guardado exitosamente'
    });
  };

  const handleDeleteFinca = (id: string) => {
    const finca = fincas.find(f => f.id === id);
    if (!finca) return;

    openModal({
      id: 'delete-finca',
      title: 'Confirmar eliminación',
      content: (
        <div>
          <p>¿Estás seguro de que quieres eliminar la finca "{finca.nombre || 'sin nombre'}"?</p>
          <p>Esta acción no se puede deshacer.</p>
        </div>
      ),
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      onConfirm: () => {
        try {
          deleteFinca(id);
          addNotification({
            type: 'success',
            title: 'Finca eliminada',
            message: 'La finca se ha eliminado exitosamente'
          });
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'No se pudo eliminar la finca'
          });
        }
      }
    });
  };

  const handleExportFincas = () => {
    try {
      const csvContent = useFincasStore.getState().exportToCSV();
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `fincas_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      addNotification({
        type: 'success',
        title: 'Exportación completada',
        message: 'Archivo CSV descargado exitosamente'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo exportar el archivo CSV'
      });
    }
  };

  const handleImportFincas = () => {
    let csvContent = '';
    
    openModal({
      id: 'import-fincas',
      title: 'Importar Fincas desde CSV',
      content: (
        <div className="import-fincas-form">
          <p>Pega el contenido del archivo CSV aquí:</p>
          <textarea 
            name="csvContent"
            rows={10}
            placeholder="ID,Número Finca,Nombre,Fecha Creación,Fecha Modificación&#10;finca_1,1,Finca Principal,2024-01-01,2024-01-01"
            style={{ width: '100%', fontFamily: 'monospace' }}
            onChange={(e) => {
              csvContent = e.target.value;
            }}
          />
          <div className="import-help">
            <h6>Formato esperado:</h6>
            <ul>
              <li>Primera fila: Headers (ID, Número Finca, Nombre, Fecha Creación, Fecha Modificación)</li>
              <li>Filas siguientes: Datos de las fincas</li>
              <li>Separador: Coma (,)</li>
              <li>Valores entre comillas dobles</li>
              <li>El número de finca es obligatorio y no debe repetirse</li>
              <li>El nombre es opcional</li>
            </ul>
          </div>
        </div>
      ),
      confirmText: 'Importar',
      cancelText: 'Cancelar',
      onConfirm: () => {
        try {
          const result = useFincasStore.getState().importFromCSV(csvContent);
          addNotification({
            type: 'success',
            title: 'Importación completada',
            message: `${result.success} fincas importadas exitosamente`
          });
          if (result.errors.length > 0) {
            addNotification({
              type: 'warning',
              title: 'Errores en importación',
              message: `${result.errors.length} errores encontrados`
            });
          }
        } catch (error) {
          addNotification({
            type: 'error',
            title: 'Error',
            message: 'No se pudo importar el archivo CSV'
          });
        }
      }
    });
  };

  // Función para manejar la ordenación
  const handleSort = (key: 'numeroFinca' | 'nombre') => {
    let direction: 'asc' | 'desc' = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  // Función para obtener el ícono de ordenación
  const getSortIcon = (key: 'numeroFinca' | 'nombre') => {
    if (sortConfig.key !== key) {
      return '⇅'; // Sin ordenar - estilo más elegante
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Función para ordenar las fincas
  const getSortedFincas = () => {
    if (!sortConfig.key) return fincas;

    return [...fincas].sort((a, b) => {
      let aValue: any = a[sortConfig.key!];
      let bValue: any = b[sortConfig.key!];

      // Para números, comparar directamente
      if (sortConfig.key === 'numeroFinca') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else {
        // Para texto, convertir a minúsculas para comparación
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  return (
    <div className="fincas-manager-content">
      <div className="fincas-manager-header">
        <h4>Gestionar Fincas Catastrales</h4>
        <p>Aquí puedes crear, editar y eliminar fincas catastrales.</p>
      </div>

      <div className="fincas-manager-actions">
        <button 
          className="btn btn-primary"
          onClick={handleCreateNewFinca}
        >
          + Nueva Finca
        </button>
        <button 
          className="btn btn-secondary"
          onClick={handleImportFincas}
        >
          Importar CSV
        </button>
        <button 
          className="btn btn-success"
          onClick={handleExportFincas}
        >
          Exportar CSV
        </button>
      </div>

      <div className="fincas-list">
        <h5>Fincas Registradas ({fincas.length})</h5>
        {fincas.length === 0 ? (
          <p className="no-fincas">No hay fincas registradas. Crea una nueva finca para comenzar.</p>
        ) : (
          <div className="fincas-table">
            <table>
              <thead>
                <tr>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('numeroFinca')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Finca {getSortIcon('numeroFinca')}
                  </th>
                  <th 
                    className="sortable-header"
                    onClick={() => handleSort('nombre')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Nombre {getSortIcon('nombre')}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {getSortedFincas().map((finca) => (
                  <tr key={finca.id}>
                    <td>Finca {finca.numeroFinca}</td>
                    <td>
                      <input
                        type="text"
                        value={finca.nombre}
                        onChange={(e) => handleUpdateFincaField(finca.id, 'nombre', e.target.value)}
                        placeholder="Introduce el nombre de la finca"
                        style={{ width: '100%', padding: '4px 8px', border: '1px solid #ddd', borderRadius: '3px' }}
                      />
                    </td>
                    <td>
                      <div className="finca-actions">
                        <button 
                          className="finca-action-btn save-btn"
                          onClick={() => handleSaveFincaChanges(finca.id)}
                          title="Guardar cambios"
                        >
                          💾
                        </button>
                        <button 
                          className="finca-action-btn edit-btn"
                          onClick={() => handleUpdateFincaField(finca.id, 'nombre', finca.nombre)}
                          title="Editar finca"
                        >
                          ✏️
                        </button>
                        <button 
                          className="finca-action-btn delete-btn"
                          onClick={() => handleDeleteFinca(finca.id)}
                          title="Eliminar finca"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Crear instancia global
export const fincasManager = new FincasManagerService();

// Hacer disponible globalmente para compatibilidad
if (typeof window !== 'undefined') {
  (window as any).fincasManager = fincasManager;
}
